import { useCallback, useEffect, useRef } from 'react'
import { useApolloClient } from '@apollo/client'
import { FEATURE_NEW_TAX_EXEMPTION_ENABLED } from '../../../../constants/wr-features'
import { settingsStore } from '../../../../factories/database/settings-store'
import { LineItem } from '../../../../models/line-item'
import {
  ProductTaxCategory,
  ProductTaxCategoryResponse,
  ProductTaxCategoryVariables,
} from '../../../../queries/product-tax-category'
import { getFeatureFromCache } from '../../../../util/feature-cache'

export const PRODUCT_TAX_CODES_KEY = 'PRODUCT_TAX_CODES'

export const useProductTaxCodes = () => {
  const client = useApolloClient()
  const cache = useRef<Record<string, string>>({})
  const hydrated = useRef(false)
  const isNewTaxExemptionEnabled = getFeatureFromCache(
    FEATURE_NEW_TAX_EXEMPTION_ENABLED
  )

  useEffect(() => {
    let cancelled = false
    const init = async () => {
      const cached = await settingsStore.get(PRODUCT_TAX_CODES_KEY)
      if (!cancelled && cached) {
        cache.current = { ...cache.current, ...cached }
      }
      hydrated.current = true
    }
    init()
    return () => {
      cancelled = true
    }
  }, [])

  const fetchProductTaxCodes = useCallback(
    async (deliveryItems: LineItem[]): Promise<Record<string, string>> => {
      if (!isNewTaxExemptionEnabled) {
        return {}
      }

      const uniqueProductIds = [
        ...new Set(
          deliveryItems
            .map(item => item.product?.id)
            .filter((id): id is string => !!id)
        ),
      ]

      const missingIds = uniqueProductIds.filter(id => !(id in cache.current))

      if (missingIds.length > 0) {
        const results = await Promise.all(
          missingIds.map(productId =>
            client
              .query<ProductTaxCategoryResponse, ProductTaxCategoryVariables>({
                query: ProductTaxCategory,
                variables: { productId },
              })
              .then(res => ({
                productId,
                taxCode: res.data?.productTaxCategory?.externalCode ?? '',
              }))
              .catch(() => ({
                productId,
                taxCode: undefined,
              }))
          )
        )

        results.forEach(({ productId, taxCode }) => {
          if (taxCode !== undefined) {
            cache.current[productId] = taxCode
          }
        })

        // Write through to IndexedDB
        settingsStore.put(PRODUCT_TAX_CODES_KEY, { ...cache.current })
      }

      return { ...cache.current }
    },
    [isNewTaxExemptionEnabled, client]
  )

  return { fetchProductTaxCodes }
}
